import { useState } from "react";
import { X, Send, Loader2, CheckCircle2, AlertCircle, Wrench } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Modal "Demander un pack métier"
 * - Public : peut être utilisé sur PacksVitrine (pas de user) et Packs (auth)
 * - POST /api/pack-requests → envoi email à contact@pitboard-app.fr via Resend
 */
export default function PackRequestModal({ open, onClose, defaultEmail = "" }) {
  const [trade, setTrade] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState(defaultEmail);
  const [status, setStatus] = useState("idle"); // idle | sending | ok | err
  const [errMsg, setErrMsg] = useState("");

  if (!open) return null;

  const reset = () => {
    setTrade(""); setDescription(""); setStatus("idle"); setErrMsg("");
  };
  const close = () => { reset(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    if (trade.trim().length < 2) {
      setStatus("err"); setErrMsg("Précisez le métier demandé (2 caractères min).");
      return;
    }
    if (!email || !email.includes("@")) {
      setStatus("err"); setErrMsg("Email invalide.");
      return;
    }
    setStatus("sending"); setErrMsg("");
    try {
      await api.post("/pack-requests", {
        trade: trade.trim(),
        description: description.trim(),
        email: email.trim(),
      });
      setStatus("ok");
    } catch (err) {
      setStatus("err");
      setErrMsg(err?.response?.data?.detail || "Erreur d'envoi. Réessayez ou écrivez à contact@pitboard-app.fr.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.72)" }}
      onClick={close}
      data-testid="pack-request-modal-backdrop"
    >
      <div
        className="w-full max-w-lg rounded-2xl border p-6 sm:p-8"
        style={{ background: "var(--carbon-2)", borderColor: "var(--steel)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="pack-request-modal"
      >
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-2">
            <Wrench size={20} style={{ color: "var(--pit-yellow)" }} />
            <h3 className="font-display text-2xl" style={{ color: "var(--chalk)", fontWeight: 700 }}>
              Demander un pack métier
            </h3>
          </div>
          <button
            onClick={close}
            className="rounded p-1 hover:bg-white/5"
            style={{ color: "var(--ink-soft)" }}
            data-testid="pack-request-close"
          >
            <X size={18} />
          </button>
        </div>
        <p className="text-sm mb-5" style={{ color: "var(--ink-soft)" }}>
          Votre métier n&apos;est pas encore dans la liste ? Dites-le nous — on peut l&apos;ajouter.
        </p>

        {status === "ok" ? (
          <div className="text-center py-6" data-testid="pack-request-success">
            <CheckCircle2 size={48} className="mx-auto mb-3" style={{ color: "#22C55E" }} />
            <h4 className="font-display text-xl mb-2" style={{ color: "var(--chalk)" }}>Demande envoyée !</h4>
            <p className="text-sm mb-6" style={{ color: "var(--ink-soft)" }}>
              Merci. On vous recontacte sous quelques jours.
            </p>
            <button onClick={close} className="btn btn-outline" data-testid="pack-request-ok-close">
              Fermer
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold mb-1.5 block" style={{ color: "var(--ink-soft)" }}>
                MÉTIER DEMANDÉ *
              </label>
              <input
                type="text"
                value={trade}
                onChange={(e) => setTrade(e.target.value)}
                placeholder="Ex. Serrurerie, Menuiserie alu, Ravalement…"
                className="input w-full"
                maxLength={80}
                data-testid="pack-request-trade"
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1.5 block" style={{ color: "var(--ink-soft)" }}>
                DÉCRIRE VOS BESOINS (optionnel)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Précisez les ouvrages principaux, les fournisseurs, les particularités…"
                className="input w-full"
                rows={4}
                maxLength={500}
                data-testid="pack-request-description"
              />
              <div className="text-[11px] mt-1 text-right" style={{ color: "var(--grey)" }}>
                {description.length}/500
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold mb-1.5 block" style={{ color: "var(--ink-soft)" }}>
                VOTRE EMAIL *
              </label>
              <input
                type="text"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.fr"
                className="input w-full"
                data-testid="pack-request-email"
              />
            </div>
            {status === "err" && (
              <div
                className="flex items-start gap-2 p-3 rounded"
                style={{ background: "rgba(225,6,0,0.12)", color: "#E10600", fontSize: "0.85rem" }}
                data-testid="pack-request-error"
              >
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{errMsg}</span>
              </div>
            )}
            <button
              type="submit"
              disabled={status === "sending"}
              className="btn btn-primary w-full"
              style={{ minHeight: 48 }}
              data-testid="pack-request-submit"
            >
              {status === "sending" ? (
                <>
                  <Loader2 size={15} className="animate-spin" /> Envoi en cours…
                </>
              ) : (
                <>
                  <Send size={15} /> Envoyer ma demande
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
